/**
 * FIELD-M1a — the data layer for the guided visit + live board chips + mobile
 * chat. Everything here is Pegasus-side (DEC-22 safe): ESC dispatches get their
 * field states in pegasus_field_status keyed by dispatch # + day.
 *
 * - useFieldStatusMap(date): one shared query (react-query dedupes the key) so
 *   the board's cards AND the tech app read the same live map.
 * - useSetFieldState: advance/undo the OTW → On site → Wrapped ladder with
 *   who/when stamps. M3's geofence time engine extends these exact stamps.
 * - useSendOtwText: the one-tap On-My-Way text — the SAME honest outbox path
 *   the office uses (held while comms_live=false, queued when live), deduped
 *   to once per stop per day.
 * - Mobile chat hooks mirror ChatDock's data model (same pegasus_dm store) in
 *   phone-sized form.
 */
import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
// OFFLINE-01: dead-zone writes queue locally and replay when signal returns.
import { enqueueOutbox, registerOutboxExecutor } from "@/lib/outbox";
import { fillTokens, firstNameOf } from "@/features/comms/comms";
// PROPERTY-LINK-01: the house's other accounts (builder / previous owner).
import { useHouseAccounts, type HouseAccount } from "@/features/customer-360/houseHistory";

/* ------------------------------------------------------------ field states */

export type FieldState = "en_route" | "on_site" | "wrapped";

/** A unit the tech marked as worked-on this visit (Equipment step). */
export interface WorkedUnit { k: string; label: string }

export interface FieldStatusRow {
  id: string;
  dispatch_ref: string;
  source: "esc" | "pegasus";
  work_date: string;
  custno: string | null;
  tech_empno: string | null;
  state: FieldState;
  en_route_at: string | null;
  on_site_at: string | null;
  wrapped_at: string | null;
  otw_sent: string | null;
  worked_equipment: WorkedUnit[] | null;
  /** ARRIVE-01: where the arrival stamp came from — device = the tech's
   *  iPad/iPhone said they were at the address, manual = the I've Arrived
   *  button, truck = Ford Pro telematics (reserved). Null on older rows. */
  on_site_source: "device" | "manual" | "truck" | null;
}

export function chicagoTodayISO(): string {
  // Techs and the shop are all America/Chicago; client-local matches.
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Live map dispatch_ref → status for a day. Board cards + the tech app share
 *  this exact queryKey, so many cards cost one fetch. */
export function useFieldStatusMap(dateISO: string) {
  return useQuery({
    queryKey: ["field_status", dateISO],
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
    queryFn: async (): Promise<Map<string, FieldStatusRow>> => {
      const { data, error } = await supabase
        .from("pegasus_field_status")
        .select("*")
        .eq("work_date", dateISO);
      if (error) throw error;
      const m = new Map<string, FieldStatusRow>();
      for (const r of (data ?? []) as FieldStatusRow[]) m.set(r.dispatch_ref, r);
      return m;
    },
  });
}

const STATE_ORDER: FieldState[] = ["en_route", "on_site", "wrapped"];
const STAMP_COL: Record<FieldState, "en_route_at" | "on_site_at" | "wrapped_at"> = {
  en_route: "en_route_at",
  on_site: "on_site_at",
  wrapped: "wrapped_at",
};

interface FieldStateInput {
  ref: string;
  source: "esc" | "pegasus";
  custno: string | null;
  locno: string | null;
  empno: string | null;
  state: FieldState | null; // null = clear the row's state back to nothing
  otw_sent?: string | null;
  /** ARRIVE-01: who says the tech arrived (only read when state = on_site). */
  on_site_source?: "device" | "manual" | "truck";
}

/** The one true state-change routine — used LIVE by the mutation and REPLAYED
 *  by the outbox executor (OFFLINE-01). `stampedAt` is when the tech actually
 *  tapped, so a dead-zone arrival keeps its real time, not the sync time. */
async function applyFieldState(
  v: FieldStateInput,
  prev: FieldStatusRow | null,
  stampedAt: string,
  email: string | null,
  workDate: string,
) {
  if (!prev) {
    if (!v.state) return;
    const { error } = await supabase.from("pegasus_field_status").insert({
      dispatch_ref: v.ref,
      source: v.source,
      work_date: workDate,
      custno: v.custno,
      locno: v.locno,
      tech_empno: v.empno,
      state: v.state,
      [STAMP_COL[v.state]]: stampedAt,
      otw_sent: v.otw_sent ?? null,
      on_site_source: v.state === "on_site" ? (v.on_site_source ?? "manual") : null,
      updated_by: email,
    });
    if (error) throw error;
    return;
  }

  // Undo past the first step = the visit never started; remove the row
  // (audit keeps the history) so the board chip disappears too.
  if (!v.state) {
    const { error } = await supabase.from("pegasus_field_status").delete().eq("id", prev.id);
    if (error) throw error;
    return;
  }

  // Existing row: advancing stamps the new state's time (kept if re-visited);
  // undoing clears the undone state's stamp so the record stays truthful.
  const patch: Record<string, unknown> = { updated_by: email, updated_at: stampedAt };
  if (v.otw_sent !== undefined) patch.otw_sent = v.otw_sent;
  patch.state = v.state;
  const idxNew = STATE_ORDER.indexOf(v.state);
  const idxOld = STATE_ORDER.indexOf(prev.state);
  if (idxNew > idxOld) {
    patch[STAMP_COL[v.state]] = prev[STAMP_COL[v.state]] ?? stampedAt;
    if (v.state === "on_site") patch.on_site_source = v.on_site_source ?? "manual";
  } else if (idxNew < idxOld) {
    patch[STAMP_COL[prev.state]] = null; // undo clears the later stamp
    if (prev.state === "on_site") patch.on_site_source = null;
  }
  const { error } = await supabase.from("pegasus_field_status").update(patch).eq("id", prev.id);
  if (error) throw error;
}

interface QueuedFieldState extends FieldStateInput {
  stamped_at: string;
  work_date: string;
}

// OFFLINE-01: how a queued status change replays once signal returns —
// re-read the CURRENT row first, so a whole dead-zone sequence
// (en route → on site → wrapped) applies cleanly in order.
registerOutboxExecutor("field_state", async (raw) => {
  const p = raw as QueuedFieldState;
  const { data } = await supabase
    .from("pegasus_field_status")
    .select("*")
    .eq("dispatch_ref", p.ref)
    .eq("work_date", p.work_date)
    .maybeSingle();
  const { data: u } = await supabase.auth.getUser();
  await applyFieldState(p, (data ?? null) as FieldStatusRow | null, p.stamped_at, u?.user?.email ?? null, p.work_date);
});

/** Optimistic cache patch so the hero/queue flips instantly while offline. */
function patchFieldCache(
  qc: QueryClient,
  workDate: string,
  v: FieldStateInput & { prev: FieldStatusRow | null },
  stampedAt: string,
) {
  qc.setQueryData<Map<string, FieldStatusRow>>(["field_status", workDate], (old) => {
    const m = new Map(old ?? []);
    if (!v.state) {
      m.delete(v.ref);
      return m;
    }
    const prev = v.prev;
    const row: FieldStatusRow = {
      id: prev?.id ?? `offline-${v.ref}`,
      dispatch_ref: v.ref,
      source: v.source,
      work_date: workDate,
      custno: v.custno,
      tech_empno: v.empno,
      state: v.state,
      en_route_at: prev?.en_route_at ?? null,
      on_site_at: prev?.on_site_at ?? null,
      wrapped_at: prev?.wrapped_at ?? null,
      otw_sent: v.otw_sent ?? prev?.otw_sent ?? null,
      worked_equipment: prev?.worked_equipment ?? null,
      on_site_source: v.state === "on_site" ? (v.on_site_source ?? "manual") : prev?.on_site_source ?? null,
    };
    const bag = row as unknown as Record<string, unknown>;
    bag[STAMP_COL[v.state]] = (prev as unknown as Record<string, unknown> | null)?.[STAMP_COL[v.state]] ?? stampedAt;
    if (prev) {
      const idxNew = STATE_ORDER.indexOf(v.state);
      const idxOld = STATE_ORDER.indexOf(prev.state);
      if (idxNew < idxOld) {
        bag[STAMP_COL[prev.state]] = null;
        if (prev.state === "on_site") row.on_site_source = null;
      }
    }
    m.set(v.ref, row);
    return m;
  });
}

const looksLikeNetworkError = (e: unknown) =>
  (typeof navigator !== "undefined" && !navigator.onLine) ||
  /fetch|network|load failed/i.test(String((e as Error | null)?.message ?? ""));

export function useSetFieldState() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: FieldStateInput & { prev: FieldStatusRow | null }) => {
      const now = new Date().toISOString();
      const today = chicagoTodayISO();
      const queueIt = () => {
        const { prev: _prev, ...rest } = v;
        enqueueOutbox("field_state", { ...rest, stamped_at: now, work_date: today } satisfies QueuedFieldState);
        patchFieldCache(qc, today, v, now);
      };
      // Dead zone: queue first, update the UI, sync when signal returns.
      if (typeof navigator !== "undefined" && !navigator.onLine) {
        queueIt();
        return;
      }
      const { data: u } = await supabase.auth.getUser();
      try {
        await applyFieldState(v, v.prev, now, u?.user?.email ?? null, today);
      } catch (e) {
        if (looksLikeNetworkError(e)) {
          queueIt();
          return;
        }
        throw e;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["field_status"] }),
  });
}

/** Persist the Equipment-step selection onto the field row the moment the
 *  tech taps it — the office sees WHAT is being worked on live, not at wrap
 *  (Seth 2026-08-27: "we want better records of what is being worked on"). */
export function useSetWorkedEquipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { fieldId: string; units: WorkedUnit[] }) => {
      const { error } = await supabase
        .from("pegasus_field_status")
        .update({ worked_equipment: v.units, updated_at: new Date().toISOString() })
        .eq("id", v.fieldId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["field_status"] }),
  });
}

/** One-tap OTW text. Returns how it landed: queued (live) / held (testing) /
 *  already (sent earlier today) / no_phone / no_template. */
export function useSendOtwText() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      ref: string;
      custno: string | null;
      locno: string | null;
      customerName: string;
      phone: string | null;
      address: string | null;
      techFirst: string | null;
    }): Promise<"queued" | "held" | "already" | "no_phone" | "no_template"> => {
      const phone10 = (v.phone ?? "").replace(/\D/g, "").slice(-10);
      if (phone10.length !== 10) return "no_phone";
      const { data: auto } = await supabase
        .from("pegasus_comms_automation")
        .select("template_sms")
        .eq("key", "appt_otw")
        .maybeSingle();
      const tpl = (auto as { template_sms: string | null } | null)?.template_sms ?? "";
      const body = fillTokens(tpl, {
        first_name: firstNameOf(v.customerName),
        tech_first: v.techFirst ?? "Our technician",
        address: v.address ?? "your home",
      }).trim();
      if (!body) return "no_template";
      const { data: gate } = await supabase.from("org_setting").select("value").eq("key", "comms_live").maybeSingle();
      const live = gate?.value === true || gate?.value === "true";
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase.from("pegasus_outbox").insert({
        automation_key: "appt_otw",
        dedupe_key: `appt_otw:${v.ref}:${chicagoTodayISO()}`,
        custno: v.custno,
        locno: v.locno,
        customer_name: v.customerName,
        channel: "sms",
        to_address: `+1${phone10}`,
        body,
        trigger_kind: "dispatch",
        trigger_ref: v.ref,
        scheduled_for: new Date().toISOString(),
        status: live ? "queued" : "held",
        created_by: u?.user?.email ?? null,
      });
      if (error) {
        if ((error as { code?: string }).code === "23505") return "already";
        throw error;
      }
      return live ? "queued" : "held";
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["comms_outbox"] });
      qc.invalidateQueries({ queryKey: ["comms_outbox_counts"] });
    },
  });
}

/** The little live pill the BOARD cards render (ESC + Pegasus alike). */
export function FieldChip({ row, dense }: { row: FieldStatusRow | undefined; dense?: boolean }) {
  if (!row) return null;
  const t = (iso: string | null) =>
    iso ? new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }).toLowerCase().replace(" ", "") : "";
  const [label, cls, time] =
    row.state === "en_route"
      ? ["OTW", "bg-amber-100 text-amber-800", t(row.en_route_at)]
      : row.state === "on_site"
        ? ["On site", "bg-emerald-100 text-emerald-800", t(row.on_site_at)]
        : ["Wrapped", "bg-slate-200 text-slate-600", t(row.wrapped_at)];
  const source =
    row.state === "on_site"
      ? row.on_site_source === "device" ? " — logged automatically from the tech's iPad/iPhone at the address"
        : row.on_site_source === "truck" ? " — from the truck (Ford Pro)"
        : row.on_site_source === "manual" ? " — the tech pressed I've Arrived"
        : ""
      : "";
  return (
    <span
      title={`From the tech's phone (Pegasus Field)${time ? ` at ${time}` : ""}${source}`}
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 font-bold uppercase tracking-wider",
        dense ? "text-[9px]" : "text-[10px]",
        cls,
      )}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {label}
      {time && <span className="font-semibold normal-case tracking-normal">{time}</span>}
    </span>
  );
}

/* ------------------------------------------------------------- mobile chat */
// Same pegasus_dm store as the desktop ChatDock — phone-sized hooks.

export interface Dm {
  id: string;
  from_email: string;
  to_email: string;
  body: string;
  created_at: string;
  read_at: string | null;
  attachment_path: string | null;
  attachment_name: string | null;
  attachment_type: string | null;
}
export interface ChatPerson {
  /** Login/DM address — empty string when nobody has put an email on file yet. */
  email: string;
  empno: string | null;
  name: string;
  /** Job description shown under the name — tech_profile.title ("CSR", "Service Technician"…). */
  role: string | null;
  /** Roster section — tech_profile.roster_group ("Office", "Service", "Install"…). */
  group: string;
}

export const SYSTEM_CHAT: ChatPerson = {
  email: "pegasus@sedgwickheating.com", empno: null, name: "Pegasus (System)",
  role: "Automated alerts", group: "Pegasus",
};
export const low = (s: string | null | undefined) => (s ?? "").trim().toLowerCase();

const TAG_RE = /\[\[(cust|job|disp|inv|po|epo):([^|\]]+)\|([^\]]+)\]\]/g;
/** Tags render as plain [Name] on the phone (tap-to-open lands at M1.5). */
export function plainBody(d: Pick<Dm, "body" | "attachment_name">): string {
  const t = (d.body ?? "").replace(new RegExp(TAG_RE.source, "g"), "[$3]").trim();
  return t || (d.attachment_name ? `📎 ${d.attachment_name}` : "");
}

/** "brentm" → "Brentm" — last-resort name when nothing better is on file. */
function prettyEmailName(email: string): string {
  return email.split("@")[0].replace(/[._-]+/g, " ").trim()
    .split(/\s+/).map((w) => w.slice(0, 1).toUpperCase() + w.slice(1)).join(" ") || email;
}

/**
 * The whole current team, for "start a chat" — built from tech_profile (the
 * staff directory: everyone current, with a job title + roster group), named
 * via mirror_employee, plus anyone with a Pegasus login (app_user) who isn't
 * on the profile list yet. app_user alone was the old source and it only holds
 * people with special permission grants — which is why searching
 * "Seth Simonson" used to find nobody (2026-08-28).
 */
export function useChatPeople(me: string) {
  return useQuery({
    queryKey: ["m_chat_people", me],
    enabled: !!me,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<ChatPerson[]> => {
      const [{ data: profs }, { data: emps }, { data: users }] = await Promise.all([
        supabase.from("tech_profile").select("empno, display_name, title, roster_group, email, end_date"),
        supabase.from("mirror_employee").select("empno, empname, firstname, lastname, email, inactive"),
        supabase.from("app_user").select("email, empno"),
      ]);
      const mir = new Map<string, { name: string; email: string | null }>();
      for (const e of (emps ?? []) as { empno: string | null; empname: string | null; firstname: string | null; lastname: string | null; email: string | null; inactive: boolean | null }[]) {
        if (!e.empno || e.inactive) continue;
        mir.set(e.empno, {
          name: e.empname || `${e.firstname ?? ""} ${e.lastname ?? ""}`.trim() || e.empno,
          email: e.email,
        });
      }
      const loginByEmpno = new Map<string, string>();
      for (const u of (users ?? []) as { email: string | null; empno: string | null }[]) {
        if (u.empno && u.email) loginByEmpno.set(u.empno, low(u.email));
      }

      const rows: ChatPerson[] = [];
      const seenEmail = new Set<string>();
      const seenEmpno = new Set<string>();

      for (const p of (profs ?? []) as { empno: string; display_name: string | null; title: string | null; roster_group: string | null; email: string | null; end_date: string | null }[]) {
        if (p.end_date) continue; // no longer with us
        const m = mir.get(p.empno);
        // Pager/SMS gateways from old ESC records aren't chat addresses.
        const mirrorEmail = m?.email && !/@(page|txt|mms)\.|@tmomail\.|@vzwpix\.|@pm\.sprint\.|@mymetropcs\./i.test(m.email) ? m.email : null;
        const email = low(p.email ?? loginByEmpno.get(p.empno) ?? mirrorEmail ?? "");
        rows.push({
          email,
          empno: p.empno,
          name: p.display_name || m?.name || (email ? prettyEmailName(email) : p.empno),
          role: p.title,
          group: p.roster_group || "Team",
        });
        seenEmpno.add(p.empno);
        if (email) seenEmail.add(email);
      }

      // Anyone with a login but no profile row yet still belongs on the list.
      for (const u of (users ?? []) as { email: string | null; empno: string | null }[]) {
        const email = low(u.email);
        if (!email || seenEmail.has(email) || (u.empno && seenEmpno.has(u.empno))) continue;
        seenEmail.add(email);
        rows.push({
          email,
          empno: u.empno,
          name: (u.empno ? mir.get(u.empno)?.name : null) ?? prettyEmailName(email),
          role: null,
          group: "Team",
        });
      }

      // NOTE: the signed-in person stays IN the list (marked "(you)" by the UI,
      // not messageable) — so searching your own name proves the search works.
      const out = rows
        .filter((p) => p.email !== SYSTEM_CHAT.email)
        .sort((a, b) => a.name.localeCompare(b.name));
      return [...out, SYSTEM_CHAT];
    },
  });
}

export function useMyDms(me: string) {
  return useQuery({
    queryKey: ["dm", me], // SAME key as the desktop dock — one cache, no drift
    enabled: !!me,
    refetchInterval: 12 * 1000,
    queryFn: async (): Promise<Dm[]> => {
      const { data } = await supabase
        .from("pegasus_dm")
        .select("*")
        .order("created_at", { ascending: true })
        .limit(2000);
      return (data ?? []) as Dm[];
    },
  });
}

export function useSendDm(me: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { to: string; body: string; attachment?: { path: string; name: string; type: string } | null }) => {
      const { error } = await supabase.from("pegasus_dm").insert({
        from_email: me,
        to_email: v.to,
        body: v.body,
        attachment_path: v.attachment?.path ?? null,
        attachment_name: v.attachment?.name ?? null,
        attachment_type: v.attachment?.type ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dm", me] }),
  });
}

// CHAT-03: per-person thread hiding — "delete" the iPhone way. Hiding stamps
// hidden_at for YOU only; messages older than the stamp vanish from your list
// AND your thread view, a newer message brings the conversation back.
export function useDmHides(me: string) {
  return useQuery({
    queryKey: ["dm_hide", me],
    enabled: !!me,
    queryFn: async (): Promise<Map<string, string>> => {
      const { data } = await supabase.from("pegasus_dm_hide").select("peer_email, hidden_at").eq("email", me);
      const m = new Map<string, string>();
      for (const r of (data ?? []) as { peer_email: string; hidden_at: string }[]) m.set(r.peer_email.toLowerCase(), r.hidden_at);
      return m;
    },
  });
}

export function useHideThread(me: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (peer: string) => {
      const { error } = await supabase.from("pegasus_dm_hide").upsert(
        { email: me, peer_email: peer.toLowerCase(), hidden_at: new Date().toISOString() },
        { onConflict: "email,peer_email" },
      );
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dm_hide", me] }),
  });
}

export function useMarkRead(me: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (from: string) => {
      const { error } = await supabase
        .from("pegasus_dm")
        .update({ read_at: new Date().toISOString() })
        .eq("to_email", me)
        .eq("from_email", from)
        .is("read_at", null);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dm", me] }),
  });
}

/** The signed-in tech's roster group (Service / Install / Warehouse / …) —
 *  drives which Today the app opens to. */
export function useRosterGroup(empno: string | null) {
  return useQuery({
    queryKey: ["m_roster_group", empno],
    enabled: !!empno,
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<string | null> => {
      const { data } = await supabase
        .from("tech_profile")
        .select("roster_group")
        .eq("empno", empno!)
        .maybeSingle();
      return (data as { roster_group: string | null } | null)?.roster_group ?? null;
    },
  });
}

/* ------------------------------------------------ call briefing (M1b) */
// The "know the customer before you knock" reads: gate/lockbox/dog notes off
// the location record, and the last visits at this address with what they cost.

export function useLocationNote(custno: string | null, locno: string | null) {
  return useQuery({
    queryKey: ["m_loc_note", custno, locno],
    enabled: !!custno,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<string | null> => {
      let q = supabase.from("mirror_location").select("locno, notes").eq("custno", custno!);
      if (locno) q = q.eq("locno", locno);
      const { data } = await q.limit(1);
      return ((data ?? [])[0] as { notes: string | null } | undefined)?.notes ?? null;
    },
  });
}

export interface VisitHistoryRow {
  invoice: string;
  dispatch: string | null;
  date: string | null;
  amount: number;
  /** What the call was about — the dispatch's problem write-up. */
  note: string | null;
  /** What we did — the invoice's resolution write-up (ESC "Tech notes" DESC
   *  lines / Pegasus tech_notes). The half Seth asked for (2026-08-27).
   *  NOTE ESC's disp_notes column is an INTEGER flag, not text — calling
   *  .trim() on it killed this whole query once (34ca). */
  workNote: string | null;
  /** What was charged — the printed billable lines. */
  charges: Array<{ d: string; amt: number }>;
  tech: string | null;
  source: "esc" | "pegasus";
}

/** ESC books the agreement check's invoice at scheduling time, so a PENDING
 *  dispatch already has an invoice row — that is not a visit that happened
 *  (FIELD-CALL-01, 2026-09-11: "Last time here · Sep 1" was tomorrow's check).
 *  Pseudo-tech buckets ("Serv Agr") are not a person either. */
const PSEUDO_TECH_RE = /^(serv\s*agr|serv|office|sales|install|warehouse|shop|unassigned)\b/i;
const personOrNull = (t: string | null | undefined) => (t && !PSEUDO_TECH_RE.test(t.trim()) ? t : null);

/** Visit history — ESC mirror invoices AND Pegasus-native ones, each with the
 *  tech who ran it, what was charged, and both write-ups. Only visits that
 *  HAPPENED: an ESC invoice whose dispatch is still pending is left out. */
export function useVisitHistory(custno: string | null, locno: string | null) {
  return useQuery({
    queryKey: ["m_visit_history", custno, locno],
    enabled: !!custno,
    staleTime: 5 * 60 * 1000,
    queryFn: () => fetchVisitHistory(custno!, locno),
  });
}

/** PROPERTY-LINK-01: the HOUSE's visits — this account's, plus every other
 *  account that has served this address (the builder before the homeowner, the
 *  previous owner), each bundle tagged with whose era it was. The call's
 *  History tab lists them under era dividers; the header counts them. */
export interface HouseVisitBundle { account: HouseAccount; rows: VisitHistoryRow[] }
export function useHouseVisitHistory(custno: string | null, locno: string | null) {
  const accounts = useHouseAccounts(custno, locno);
  const others = useMemo(() => (accounts.data ?? []).filter((a) => !a.isSelf && a.visitCount > 0), [accounts.data]);
  const key = others.map((a) => `${a.custno}/${a.locno ?? ""}`).join(",");
  const bundles = useQuery({
    queryKey: ["m_house_visits", custno, locno, key],
    enabled: !!custno && others.length > 0,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<HouseVisitBundle[]> => {
      const out = await Promise.all(others.map(async (account) => ({ account, rows: await fetchVisitHistory(account.custno, account.locno) })));
      // the most recently served other account first (a builder's 2024 visit above a 2018 owner's)
      return out.filter((b) => b.rows.length > 0).sort((a, b) => (b.rows[0]?.date ?? "").localeCompare(a.rows[0]?.date ?? ""));
    },
  });
  return {
    accounts: accounts.data ?? [],
    self: (accounts.data ?? []).find((a) => a.isSelf) ?? null,
    others: others.length > 0 ? (bundles.data ?? []) : [],
    isLoading: accounts.isLoading || (others.length > 0 && bundles.isLoading),
  };
}

async function fetchVisitHistory(custno: string, locno: string | null): Promise<VisitHistoryRow[]> {
  {
    {
      let q = supabase
        .from("mirror_sales")
        .select("invoice, dispatch, inv_date, inv_amount")
        .eq("custno", custno!)
        .order("inv_date", { ascending: false })
        .limit(10);
      if (locno) q = q.eq("locno", locno);
      let pq = supabase
        .from("pegasus_job_invoice")
        .select("id, invoice_no, dispatch_no, completed_date, total, tech_notes, technician, status")
        .eq("custno", custno!)
        .in("status", ["finalized", "done", "issued", "paid"])
        .order("completed_date", { ascending: false })
        .limit(10);
      if (locno) pq = pq.eq("locno", locno);
      const [{ data }, { data: pdata }] = await Promise.all([q, pq]);
      const rows = ((data ?? []) as { invoice: string; dispatch: string | null; inv_date: string | null; inv_amount: number | null }[])
        .filter((r) => r.inv_date);

      const dispNos = [...new Set(rows.map((r) => r.dispatch).filter(Boolean))] as string[];
      const invNos = rows.map((r) => r.invoice);
      const pegRows = ((pdata ?? []) as { id: string; invoice_no: string | null; dispatch_no: string | null; completed_date: string | null; total: number | null; tech_notes: string | null; technician: string | null }[])
        .filter((r) => r.completed_date);

      const noteBy = new Map<string, string>();
      const techBy = new Map<string, string>();
      const workBy = new Map<string, string>();
      const chargeBy = new Map<string, Array<{ d: string; amt: number }>>();
      const pegChargeBy = new Map<string, Array<{ d: string; amt: number }>>();
      const pendingDisp = new Set<string>();

      await Promise.all([
        (async () => {
          if (!dispNos.length) return;
          const { data: d } = await supabase
            .from("v_dispatch_full")
            .select("dispatch, notes, tech_name, tech_first, status")
            .in("dispatch", dispNos);
          for (const x of (d ?? []) as { dispatch: string; notes: string | null; tech_name: string | null; tech_first: string | null; status: string | null }[]) {
            if (!noteBy.has(x.dispatch) && x.notes) noteBy.set(x.dispatch, x.notes);
            const t = personOrNull(x.tech_first || x.tech_name);
            if (!techBy.has(x.dispatch) && t) techBy.set(x.dispatch, t);
            if (/^(pend|sched|assign|dispatch|open)/i.test((x.status ?? "").trim())) pendingDisp.add(x.dispatch);
          }
        })(),
        (async () => {
          // ESC invoice lines: DESC rows are the write-up; priced printed rows
          // are what was charged.
          if (!invNos.length) return;
          const { data: ld } = await supabase
            .from("mirror_sales_line")
            .select("invoice, prod, line_desc, amount, no_print")
            .in("invoice", invNos)
            .order("line_count", { ascending: true });
          for (const l of (ld ?? []) as { invoice: string; prod: string | null; line_desc: string | null; amount: number | null; no_print: boolean | null }[]) {
            const isDesc = (l.prod ?? "").toUpperCase() === "DESC";
            const txt = (l.line_desc ?? "").trim();
            if (isDesc && txt) {
              workBy.set(l.invoice, workBy.has(l.invoice) ? `${workBy.get(l.invoice)}\n${txt}` : txt);
            } else if (!isDesc && txt && !l.no_print && Number(l.amount ?? 0) !== 0) {
              const arr = chargeBy.get(l.invoice) ?? [];
              if (arr.length < 8) arr.push({ d: txt, amt: Number(l.amount ?? 0) });
              chargeBy.set(l.invoice, arr);
            }
          }
        })(),
        (async () => {
          const ids = pegRows.map((r) => r.id);
          if (!ids.length) return;
          const { data: pl } = await supabase
            .from("pegasus_job_invoice_line")
            .select("invoice_id, description, amount, print")
            .in("invoice_id", ids)
            .order("sort", { ascending: true });
          for (const l of (pl ?? []) as { invoice_id: string; description: string | null; amount: number | null; print: boolean | null }[]) {
            const txt = (l.description ?? "").trim();
            if (!txt || l.print === false) continue;
            const arr = pegChargeBy.get(l.invoice_id) ?? [];
            if (arr.length < 8) arr.push({ d: txt, amt: Number(l.amount ?? 0) });
            pegChargeBy.set(l.invoice_id, arr);
          }
        })(),
      ]);

      const esc: VisitHistoryRow[] = rows.filter((r) => !(r.dispatch && pendingDisp.has(r.dispatch))).map((r) => ({
        invoice: r.invoice,
        dispatch: r.dispatch,
        date: r.inv_date,
        amount: Number(r.inv_amount ?? 0),
        note: r.dispatch ? (noteBy.get(r.dispatch) ?? null) : null,
        workNote: workBy.get(r.invoice) ?? null,
        charges: chargeBy.get(r.invoice) ?? [],
        tech: r.dispatch ? (techBy.get(r.dispatch) ?? null) : null,
        source: "esc",
      }));
      const peg: VisitHistoryRow[] = pegRows.map((r) => ({
        invoice: r.invoice_no || r.id.slice(0, 8),
        dispatch: r.dispatch_no,
        date: r.completed_date,
        amount: Number(r.total ?? 0),
        note: null,
        workNote: r.tech_notes?.trim() || null,
        charges: pegChargeBy.get(r.id) ?? [],
        tech: r.technician,
        source: "pegasus",
      }));
      return [...peg, ...esc]
        .sort((a, b) => (b.date ?? "").localeCompare(a.date ?? ""))
        .slice(0, 12);
    }
  }
}

/* --------------------------------------- customer contacts (parity pass) */
// Everything the office 360 knows how to reach them by — account phones with
// their ESC labels, location contacts, and every email on file — so the truck
// stops being the least-informed screen in the company (Seth 2026-08-27).

export interface CustomerContacts {
  fullname: string | null;
  add1: string | null;
  add2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  phones: Array<{ n: string; label: string }>;
  emails: Array<{ e: string; label: string }>;
}

export function useCustomerContacts(custno: string | null, locno: string | null) {
  return useQuery({
    queryKey: ["m_cust_contacts", custno, locno],
    enabled: !!custno,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<CustomerContacts | null> => {
      let lq = supabase
        .from("mirror_location")
        .select("locname, add1, city, state, zip, phone1, phone2, phone3, phone4, phone5, phone6, contact1, contact2, contact3, contact4, contact5, contact6, email, email2, email3, email4, email5, email6")
        .eq("custno", custno!);
      if (locno) lq = lq.eq("locno", locno);
      const [{ data: cust }, { data: locs }, { data: pegEmail }] = await Promise.all([
        supabase
          .from("mirror_customer")
          .select("fullname, add1, add2, city, state, zip, phone1, phone2, phone3, phone4, lbl_phone1, lbl_phone2, lbl_phone3, lbl_phone4")
          .eq("custno", custno!)
          .maybeSingle(),
        lq.limit(1),
        supabase
          .from("pegasus_customer_email")
          .select("email")
          .eq("custno", custno!)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);
      const c = cust as Record<string, string | null> | null;
      const l = ((locs ?? [])[0] ?? null) as Record<string, string | null> | null;
      if (!c && !l) return null;
      const clean = (s: string | null | undefined) => (s ?? "").trim() || null;
      const phones: Array<{ n: string; label: string }> = [];
      const seenP = new Set<string>();
      const pushPhone = (n: string | null | undefined, label: string) => {
        const v = clean(n);
        if (!v) return;
        const key = v.replace(/[^0-9]/g, "");
        if (key.length < 7 || seenP.has(key)) return;
        seenP.add(key);
        phones.push({ n: v, label });
      };
      if (c) {
        pushPhone(c.phone1, clean(c.lbl_phone1) ?? "Main");
        pushPhone(c.phone2, clean(c.lbl_phone2) ?? "Phone 2");
        pushPhone(c.phone3, clean(c.lbl_phone3) ?? "Phone 3");
        pushPhone(c.phone4, clean(c.lbl_phone4) ?? "Phone 4");
      }
      if (l) {
        pushPhone(l.phone1, clean(l.contact1) ?? "Location");
        pushPhone(l.phone2, clean(l.contact2) ?? "Location 2");
        pushPhone(l.phone3, clean(l.contact3) ?? "Location 3");
        pushPhone(l.phone4, clean(l.contact4) ?? "Location 4");
        pushPhone(l.phone5, clean(l.contact5) ?? "Location 5");
        pushPhone(l.phone6, clean(l.contact6) ?? "Location 6");
      }
      const emails: Array<{ e: string; label: string }> = [];
      const seenE = new Set<string>();
      const pushEmail = (e: string | null | undefined, label: string) => {
        const v = clean(e);
        if (!v || !/\S+@\S+\.\S+/.test(v) || seenE.has(v.toLowerCase())) return;
        seenE.add(v.toLowerCase());
        emails.push({ e: v, label });
      };
      pushEmail((pegEmail as { email: string | null } | null)?.email, "Newest on file");
      if (l) {
        pushEmail(l.email, "On file");
        pushEmail(l.email2, "On file");
        pushEmail(l.email3, "On file");
        pushEmail(l.email4, "On file");
        pushEmail(l.email5, "On file");
        pushEmail(l.email6, "On file");
      }
      return {
        fullname: clean(c?.fullname),
        add1: clean(c?.add1) ?? clean(l?.add1),
        add2: clean(c?.add2),
        city: clean(c?.city) ?? clean(l?.city),
        state: clean(c?.state) ?? clean(l?.state),
        zip: clean(c?.zip) ?? clean(l?.zip),
        phones,
        emails,
      };
    },
  });
}

/** One-shot device location (foreground) — powers auto-arrive when the tech
 *  opens the call while standing at the address. Never blocks the UI. */
export function getPosition(timeoutMs = 6000): Promise<{ lat: number; lng: number } | null> {
  return new Promise((resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 30000 },
    );
  });
}
